///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import { findIndex, orderBy, remove } from "lodash";

import {
  getBidSummary,
  acceptBid,
  archiveBid,
  closeAllModesForBidLost,
  closeAllModesForBidLostWithCrm,
  closeBidWon as closeBidWonApi,
  closeBidMode,
  getBidShipperHistory
} from "../api/bids";
import {
  getBidTeam,
  putTeamContact,
  postBidSubscriber,
  deleteBidSubscriber
} from "../api/persons";
import {
  downloadAttachment,
  getBidAttachments,
  deleteAttachment
} from "../api/attachments";
import { getBidMessages } from "../api/messages";
import {
  mode as modetypes,
  userRole,
  fileStatus,
  order as orderTypes,
  rfqStatusTypes,
  httpResponseStatus
} from "../helpers/types";
import saveFile from "../helpers/saveFile";
import {
  getTLLaneCountByRegion,
  getAwardedLanes,
  getShipperRatesAwardedLanes,
  getRevenue
} from "../api/lanes";
import formatMessage from "../helpers/formatMessage";
import { axiosAll } from "../api/axios";
import { fetchBidDetails } from "./bidDetailsSlice";
import {
  fetchPricingLanes,
  receivePricingLanesFailure,
  setLoading
} from "./bidPricingSlice";
import { getShipperRatesStatus } from "../api/shippers";

export const initialState = {
  newBidFileUploadError: false,
  summaryLoading: false,
  teamLoading: false,
  messagesLoading: false,
  attachmentsLoading: false,
  shipperHistoryLoading: false,

  summary: null,
  team: null,
  messages: null,
  attachments: null,
  shipperHistory: null,

  summaryError: false,
  teamError: false,
  messagesError: false,
  attachmentsError: false,
  shipperHistoryError: false,

  documentDownloadError: false,
  documents: [],
  allDocumentsUploaded: false,

  showBanner: false,
  tlLaneCountByRegion: {
    isFetching: false,
    isFailure: false,
    lanesByRegion: []
  },
  initialLoadError: null,
  acceptActionIsLoading: false,
  acceptActionFailure: false,
  archiveActionIsLoading: false,
  archiveActionFailure: false,
  closeBidActionIsLoading: false,
  closeBidActionFailure: false,
  closeBidWonActionIsLoading: false,
  closeBidWonActionFailure: false,
  isAwardTemplateDownloading: false,
  awardTemplateDownloadFailed: false,
  closeBidModeActionIsLoading: false,
  closeBidModeActionFailure: false,
  teamEdit: {
    modalOpen: false,
    isLoading: false,
    isError: false,
    isForbidden: false,
    teamMembers: null
  },
  deleteItem: {
    isLoading: false,
    isFailure: false,
    modalOpen: false,
    item: null,
    itemType: null
  },
  shipperHistorySort: {
    field: null,
    order: null
  },
  revenue: null,
  shipperRatesEnabled: false
};

const handleSummarySectionRequest = (state, field) => {
  state[field] = null;
  state[field + "Loading"] = true;
  state[field + "Error"] = false;
  state.showBanner = true;
};

const handleSummarySectionSuccess = (state, field, payload) => {
  state[field] = payload;
  state[field + "Loading"] = false;
  state[field + "Error"] = false;
  state.showBanner = true;
};

const handleSummarySectionFailure = (state, field) => {
  state[field] = null;
  state[field + "Loading"] = false;
  state[field + "Error"] = true;
  if (state.initialLoadError === null) {
    state.initialLoadError = true;
  }
  state.showBanner = true;
};

const bidSummarySlice = createSlice({
  name: "bidSummary",
  initialState,
  reducers: {
    toggleNewBidFileUploadError: (state, { payload }) => {
      state.newBidFileUploadError = payload;
    },
    hideBanner: state => {
      state.showBanner = false;
    },

    requestBidSummary: state => handleSummarySectionRequest(state, "summary"),
    requestBidTeam: state => handleSummarySectionRequest(state, "team"),
    requestBidMessages: state => handleSummarySectionRequest(state, "messages"),
    requestBidAttachments: state =>
      handleSummarySectionRequest(state, "attachments"),
    requestBidShipperHistory: state =>
      handleSummarySectionRequest(state, "shipperHistory"),

    receiveBidSummarySuccess: (state, { payload: { summary, role } }) => {
      if (summary) {
        let firstMode = null;
        const modes = summary.modes.filter(mode => {
          if (
            mode.mode === modetypes.tl &&
            (role === userRole.tlPricingAnalyst ||
              role === userRole.assetBasedPricingUser)
          ) {
            firstMode = mode;
            return false;
          } else if (
            mode.mode === modetypes.ltl &&
            role === userRole.ltlPricingAnalyst
          ) {
            firstMode = mode;
            return false;
          } else if (
            mode.mode === modetypes.imdl &&
            role === userRole.imdlPricingAnalyst
          ) {
            firstMode = mode;
            return false;
          }
          return true;
        });
        firstMode && modes.unshift(firstMode);
        summary.modes = modes;
      }
      handleSummarySectionSuccess(state, "summary", summary);
    },
    receiveBidTeamSuccess: (state, { payload }) =>
      handleSummarySectionSuccess(state, "team", payload),
    receiveBidMessagesSuccess: (state, { payload: messages }) => {
      messages.length &&
        messages.map(({ userMessage }) => formatMessage(userMessage));
      handleSummarySectionSuccess(
        state,
        "messages",
        messages.length ? orderBy(messages, "createDateUTC") : messages
      );
    },
    receiveBidAttachmentsSuccess: (state, { payload }) =>
      handleSummarySectionSuccess(
        state,
        "attachments",
        payload.attachments?.length
          ? {
              attachments: orderBy(payload.attachments, "uploadDateUTC", "desc")
            }
          : payload
      ),
    receiveBidShipperHistorySuccess: (state, { payload }) =>
      handleSummarySectionSuccess(state, "shipperHistory", {
        ...payload,
        bidShipperHistories: payload.bidShipperHistories.map((bid, index) => ({
          ...bid,
          statusText: rfqStatusTypes.find(x => x.value === bid.status).label,
          defaultIndex: index
        }))
      }),

    receiveBidSummaryFailure: state =>
      handleSummarySectionFailure(state, "summary"),
    receiveBidTeamFailure: state => handleSummarySectionFailure(state, "team"),
    receiveBidMessagesFailure: state =>
      handleSummarySectionFailure(state, "messages"),
    receiveBidAttachmentsFailure: state =>
      handleSummarySectionFailure(state, "attachments"),
    receiveBidShipperHistoryFailure: state =>
      handleSummarySectionFailure(state, "shipperHistory"),

    closeInitialLoadErrorModal: state => {
      state.initialLoadError = false;
    },

    addDocumentsToUpload: (state, { payload: files }) => {
      state.documents = state.documents.concat(files);
      state.allDocumentsUploaded = false;
    },
    removeDocumentToUpload: (state, { payload: { id, removeAll } }) => {
      if (id) {
        const index = state.documents.findIndex(document => document.id === id);
        if (index !== -1) {
          state.documents[index].cancelToken &&
            state.documents[index].cancelToken();
          const filteredDocs = state.documents.filter(f => f.id !== id);
          state.documents = filteredDocs;
          if (
            filteredDocs.length &&
            filteredDocs.every(d => d.status === fileStatus.uploaded)
          ) {
            state.allDocumentsUploaded = true;
          }
        }
      } else if (removeAll) {
        state.documents.every(d => d.cancelToken && d.cancelToken());
        state.documents = [];
        state.allDocumentsUploaded = false;
      }
    },
    setAllDocumentsUploaded: (state, { payload }) => {
      state.allDocumentsUploaded = payload;
    },
    documentUploadEvent: (
      state,
      { payload: { id, status, complete, cancelToken, conflictingFile } }
    ) => {
      const index = state.documents.findIndex(document => document.id === id);

      if (index !== -1) {
        state.documents[index].status = status;
        state.documents[index].complete = complete;
        state.documents[index].conflictingFile = conflictingFile;
        if (cancelToken) {
          state.documents[index].cancelToken = cancelToken;
        }
        if (state.documents.every(d => d.status === fileStatus.uploaded)) {
          state.allDocumentsUploaded = true;
        }
      }
    },
    toggleDocumentDownloadError: (state, { payload }) => {
      state.documentDownloadError = payload;
    },
    toggleDownloadIndicator: (state, { payload: { name, indicator } }) => {
      if (state.attachments && state.attachments.attachments) {
        const index = state.attachments.attachments.findIndex(
          x => x.fileName === name
        );
        if (index !== -1) {
          state.attachments.attachments[index].downloadIndicator = indicator;
        }
      }
    },

    acceptActionRequest: state => {
      state.acceptActionIsLoading = true;
      state.acceptActionFailure = false;
    },
    acceptActionSuccess: state => {
      state.acceptActionIsLoading = false;
      state.acceptActionFailure = false;
    },
    acceptActionFailure: state => {
      state.acceptActionIsLoading = false;
      state.acceptActionFailure = true;
    },
    archiveActionRequest: state => {
      state.archiveActionIsLoading = true;
      state.archiveActionFailure = false;
    },
    archiveActionSuccess: state => {
      state.archiveActionIsLoading = false;
      state.archiveActionFailure = false;
    },
    archiveActionFailure: state => {
      state.archiveActionIsLoading = false;
      state.archiveActionFailure = true;
    },
    closeBidActionRequest: state => {
      state.closeBidActionIsLoading = true;
      state.closeBidActionFailure = false;
    },
    resetCloseBidActionState: state => {
      state.closeBidActionIsLoading = false;
      state.closeBidActionFailure = false;
    },
    closeBidActionFailure: state => {
      state.closeBidActionIsLoading = false;
      state.closeBidActionFailure = true;
    },
    closeBidWonActionRequest: state => {
      state.closeBidWonActionIsLoading = true;
      state.closeBidWonActionFailure = false;
    },
    resetCloseBidWonActionState: state => {
      state.closeBidWonActionIsLoading = false;
      state.closeBidWonActionFailure = false;
    },
    closeBidWonActionFailure: state => {
      state.closeBidWonActionIsLoading = false;
      state.closeBidWonActionFailure = true;
    },
    downloadAwardedLanesRequest: state => {
      state.isAwardTemplateDownloading = true;
      state.awardTemplateDownloadFailed = false;
    },
    downloadAwardedLanesSuccess: state => {
      state.isAwardTemplateDownloading = false;
      state.awardTemplateDownloadFailed = false;
    },
    downloadAwardedLanesFailure: state => {
      state.isAwardTemplateDownloading = false;
      state.awardTemplateDownloadFailed = true;
    },
    closeBidModeActionRequest: state => {
      state.closeBidModeActionIsLoading = true;
      state.closeBidModeActionFailure = false;
    },
    resetCloseBidModeActionState: state => {
      state.closeBidModeActionIsLoading = false;
      state.closeBidModeActionFailure = false;
    },
    closeBidModeActionFailure: state => {
      state.closeBidModeActionIsLoading = false;
      state.closeBidModeActionFailure = true;
    },
    resetBannerActionState: state => {
      state.acceptActionIsLoading = false;
      state.acceptActionFailure = false;
      state.archiveActionIsLoading = false;
      state.archiveActionFailure = false;
      state.isAwardTemplateDownloading = false;
      state.awardTemplateDownloadFailed = false;
    },

    tlLaneCountByRegionRequest: state => {
      state.tlLaneCountByRegion.isFetching = true;
      state.tlLaneCountByRegion.isFailure = false;
    },
    tlLaneCountByRegionSuccess: (state, { payload }) => {
      state.tlLaneCountByRegion.isFetching = false;
      state.tlLaneCountByRegion.lanesByRegion = payload;
    },
    tlLaneCountByRegionFailure: state => {
      state.tlLaneCountByRegion.isFetching = false;
      state.tlLaneCountByRegion.isFailure = true;
    },
    assignRegion: (state, { payload: { user, regionId } }) => {
      let region = state.tlLaneCountByRegion.lanesByRegion.find(
        r => r.regionId === regionId
      );
      region.newUser = user;
      region.edited = true;
    },

    openTeamEditModal: state => {
      state.teamEdit.modalOpen = true;
      const teamMembers = { ...state.team };
      if (teamMembers.directorOfCustomerSuccessAndOpportunityLeader) {
        const combinedUser = teamMembers.directorOfCustomerSuccessAndOpportunityLeader;
        teamMembers.directorOfCustomerSuccess = {
          ...combinedUser,
          jobTitle: combinedUser.jobTitle
        };
        teamMembers.opportunityLeader = {
          ...combinedUser,
          jobTitle: combinedUser.secondaryJobTitle
        };
      }
      
      state.teamEdit.teamMembers = teamMembers;
    },
    updateTeamMembers: (state, { payload: { newContact, field } }) => {
      let members = state.teamEdit.teamMembers;
      if (newContact.userId && state.team[field]?.userId !== newContact.userId) {
        const isError = members["subscribers"]
          .filter(r => r.isRemoved !== true)
          .some(x => x.userId === newContact.userId);
        members[field] = {
          ...newContact,
          isUpdated: true,
          error: isError
        };
      } else {
        members[field] = state.team[field] || { userId: null, fullName: "" };
      }
      state.teamEdit.teamMembers = members;
    },
    addNewSubscriber: (state, { payload: { newSubscriber } }) => {
      state.teamEdit.teamMembers["subscribers"].push(newSubscriber);
    },
    setNewSubscriber: (state, { payload: { newSubscriber, id } }) => {
      //check if the user is already present in team members list
      let teamMembers = Object.values(state.teamEdit.teamMembers);
      let isExist = teamMembers.some(
        t => t && t.userId === newSubscriber.userId
      );
      if (isExist) {
        newSubscriber.error = true;
      }

      //check if the user is already present in subscribers list
      let subscribers = state.teamEdit.teamMembers["subscribers"].filter(
        x => x.userId !== null && x.isRemoved !== true && x.id !== id
      );
      isExist = subscribers.some(s => s.userId === newSubscriber.userId);
      if (isExist) {
        newSubscriber.error = true;
      }

      //replace the new values of subscriber
      let index = findIndex(state.teamEdit.teamMembers["subscribers"], {
        id: id
      });
      state.teamEdit.teamMembers["subscribers"].splice(index, 1, {
        ...newSubscriber,
        id: id,
        isNewSubscriber: true
      });
    },
    removeSubscriber: (state, { payload: { subscriber } }) => {
      if (subscriber.isNewSubscriber) {
        remove(state.teamEdit.teamMembers["subscribers"], s => {
          return s.id === subscriber.id;
        });
      } else {
        let index = findIndex(state.teamEdit.teamMembers["subscribers"], {
          userId: subscriber.userId
        });
        state.teamEdit.teamMembers["subscribers"].splice(index, 1, {
          ...subscriber,
          isRemoved: true
        });
      }
    },
    handleSubscriberValidation: (state, { payload: { subscriber } }) => {
      //check if an error exists for team members
      let isExist = false;
      let keys = Object.keys(state.teamEdit.teamMembers);
      keys
        .filter(c => c !== "subscribers")
        .map(k => {
          let value = state.teamEdit.teamMembers[k];
          if (value && value.userId === subscriber.userId && value.error) {
            isExist = true;
            value.error = false;
            state.teamEdit.teamMembers[k] = value;
          }
          return state.teamEdit.teamMembers[k];
        });

      //check if an error exists for subscribers
      if (!isExist) {
        state.teamEdit.teamMembers["subscribers"].map(s => {
          if (s.userId === subscriber.userId && s.error && !isExist) {
            s.error = false;
            isExist = true;
          }
          return false;
        });
      }
    },
    requestTeamEdit: state => {
      state.teamEdit.isLoading = true;
      state.teamEdit.isError = false;
      state.teamEdit.isForbidden = false;
      state.teamEdit.modalOpen = true;
    },
    receiveTeamEditFailure: (state, { payload }) => {
      state.teamEdit.isLoading = false;
      state.teamEdit.isError = !payload?.isForbidden;
      state.teamEdit.isForbidden = payload?.isForbidden || false;
      state.teamEdit.modalOpen = false;
    },
    resetTeamEditState: state => {
      state.teamEdit = initialState.teamEdit;
    },

    openDeleteConfirmationModal: (state, { payload: { item, itemType } }) => {
      state.deleteItem.modalOpen = true;
      state.deleteItem.item = item;
      state.deleteItem.itemType = itemType;
    },
    requestDeleteItem: state => {
      state.deleteItem.isLoading = true;
      state.deleteItem.isFailure = false;
    },
    receiveDeleteItemFailure: state => {
      state.deleteItem.isLoading = false;
      state.deleteItem.isFailure = true;
    },
    resetDeleteItemState: state => {
      state.deleteItem = initialState.deleteItem;
    },
    sortShipperHistory: (state, { payload: { order, field } }) => {
      state.shipperHistorySort.order = order;
      state.shipperHistorySort.field = field;
      state.shipperHistory.bidShipperHistories = orderBy(
        state.shipperHistory.bidShipperHistories,
        [field ? field : "defaultIndex"],
        [order ? order : orderTypes.ascending]
      );
    },
    setRevenue: (state, { payload }) => {
      state.revenue = payload;
    },
    setShipperRatesEnabled: (state, { payload }) => {
      state.shipperRatesEnabled = payload;
    },
    resetBidSummary: state => {
      state.summary = null;
    }
  }
});

export const {
  toggleNewBidFileUploadError,
  hideBanner,

  requestBidSummary,
  receiveBidSummarySuccess,
  receiveBidSummaryFailure,

  requestBidTeam,
  receiveBidTeamSuccess,
  receiveBidTeamFailure,

  requestBidMessages,
  receiveBidMessagesSuccess,
  receiveBidMessagesFailure,

  requestBidAttachments,
  receiveBidAttachmentsSuccess,
  receiveBidAttachmentsFailure,

  requestBidShipperHistory,
  receiveBidShipperHistorySuccess,
  receiveBidShipperHistoryFailure,

  closeInitialLoadErrorModal,

  addDocumentsToUpload,
  removeDocumentToUpload,
  setAllDocumentsUploaded,
  documentUploadEvent,
  toggleDocumentDownloadError,
  toggleDownloadIndicator,

  acceptActionFailure,
  acceptActionRequest,
  acceptActionSuccess,
  archiveActionFailure,
  archiveActionRequest,
  archiveActionSuccess,
  closeBidActionFailure,
  closeBidActionRequest,
  resetCloseBidActionState,
  closeBidWonActionFailure,
  closeBidWonActionRequest,
  resetCloseBidWonActionState,
  downloadAwardedLanesRequest,
  downloadAwardedLanesSuccess,
  downloadAwardedLanesFailure,
  closeBidModeActionFailure,
  closeBidModeActionRequest,
  resetCloseBidModeActionState,
  resetBannerActionState,

  tlLaneCountByRegionRequest,
  tlLaneCountByRegionSuccess,
  tlLaneCountByRegionFailure,
  assignRegion,

  openTeamEditModal,
  updateTeamMembers,
  addNewSubscriber,
  setNewSubscriber,
  removeSubscriber,
  handleSubscriberValidation,
  requestTeamEdit,
  receiveTeamEditFailure,
  resetTeamEditState,

  openDeleteConfirmationModal,
  requestDeleteItem,
  receiveDeleteItemFailure,
  resetDeleteItemState,
  sortShipperHistory,
  setRevenue,
  setShipperRatesEnabled,
  resetBidSummary
} = bidSummarySlice.actions;

export const bidSummaryReducer = bidSummarySlice.reducer;

export const fetchBidSummary =
  ({
    bidId,
    role,
    fetchTLLaneCount = false,
    division,
    fetchLanes,
    shouldFetchMatchingLanes = true,
    clearFilterAndSortDetails = true,
    loadRxoTlPricingFilterFacets = false
  }) =>
  dispatch => {
    if (
      fetchLanes &&
      role !== userRole.carrierRelationsRepresentative &&
      role !== userRole.procurementRepresentative
    ) {
      dispatch(
        setLoading({
          role,
          division,
          clearFilterAndSortDetails,
          shouldFetchMatchingLanes
        })
      );
    }

    dispatch(requestBidSummary());
    getBidSummary(bidId)
      .then(({ data }) => {
        dispatch(receiveBidSummarySuccess({ summary: data, role }));
        if (
          fetchLanes &&
          role !== userRole.carrierRelationsRepresentative &&
          role !== userRole.procurementRepresentative
        ) {
          dispatch(
            fetchPricingLanes({
              bidId,
              role,
              clearFilterAndSortDetails,
              shouldFetchMatchingLanes,
              bidDivision: data.division,
              isCoyoteBid: data.isCoyoteBid,
              loadRxoTlPricingFilterFacets
            })
          );
        }
        if (
          data?.modes.some(mode => mode.mode === modetypes.tl) &&
          (role === userRole.tlPricingAnalyst ||
            role === userRole.assetBasedPricingUser) &&
          fetchTLLaneCount
        ) {
          dispatch(fetchTLLaneCountByRegion(bidId));
        }
        data.shipperId && dispatch(fetchShipperRatesStatus(data.shipperId));
      })
      .catch(() => {
        dispatch(receiveBidSummaryFailure());
        if (
          fetchLanes &&
          role !== userRole.carrierRelationsRepresentative &&
          role !== userRole.procurementRepresentative
        ) {
          dispatch(receivePricingLanesFailure());
        }
      });
  };

export const fetchBidTeam = bidId => async dispatch => {
  dispatch(requestBidTeam());

  try {
    let team = (await getBidTeam(bidId)).data;
    
    if (team.subscribers && team.subscribers.length > 0) {
      const directorOfCustomerSuccess = team.subscribers.find(s => s.isDirectorOfCustomerSuccess);
      const opportunityLeader = team.subscribers.find(s => s.isOpportunityLeader);
      
      const isSameUser = directorOfCustomerSuccess && opportunityLeader && 
                         directorOfCustomerSuccess.userId === opportunityLeader.userId;
      
      team.subscribers = team.subscribers.filter(
        s => !s.isDirectorOfCustomerSuccess && !s.isOpportunityLeader
      );
      
      if (isSameUser) {
        team.directorOfCustomerSuccessAndOpportunityLeader = {
          ...directorOfCustomerSuccess,
          jobTitle: "Director of Customer Success",
          secondaryJobTitle: "Opportunity Leader"
        };
      } else {
        if (directorOfCustomerSuccess) {
          team.directorOfCustomerSuccess = {
            ...directorOfCustomerSuccess,
            jobTitle: "Director of Customer Success"
          };
        }
        if (opportunityLeader) {
          team.opportunityLeader = {
            ...opportunityLeader,
            jobTitle: "Opportunity Leader"
          };
        }
      }
    }
    
    dispatch(receiveBidTeamSuccess(team));
  } catch {
    dispatch(receiveBidTeamFailure());
  }
};

export const fetchBidMessages = bidId => dispatch => {
  dispatch(requestBidMessages());
  getBidMessages(bidId)
    .then(({ data }) => dispatch(receiveBidMessagesSuccess(data)))
    .catch(() => dispatch(receiveBidMessagesFailure()));
};

export const fetchBidShipperHistory = bidId => async (dispatch, getState) => {
  const { user } = getState().user;
  dispatch(requestBidShipperHistory());
  getBidShipperHistory(bidId, user.userRoleForManagementUser)
    .then(({ data }) => dispatch(receiveBidShipperHistorySuccess(data)))
    .catch(() => dispatch(receiveBidShipperHistoryFailure()));
};

export const fetchBidAttachments = bidId => async dispatch => {
  dispatch(requestBidAttachments());

  try {
    let attachments = (await getBidAttachments(bidId)).data;
    dispatch(receiveBidAttachmentsSuccess(attachments));
  } catch {
    dispatch(receiveBidAttachmentsFailure());
  }
};

export const downloadBidAttachment = (name, bidId) => dispatch => {
  dispatch(toggleDownloadIndicator({ name, indicator: true }));
  downloadAttachment(name, bidId)
    .then(response => {
      dispatch(toggleDownloadIndicator({ name, indicator: false }));
      saveFile(response.data, name);
    })
    .catch(() => {
      dispatch(toggleDownloadIndicator({ name, indicator: false }));
      dispatch(toggleDocumentDownloadError(true));
    });
};

export const fetchBidSummaryTabDetails = (bidId, role) => dispatch => {
  dispatch(fetchBidSummary({ bidId, role, fetchTLLaneCount: true }));
  dispatch(fetchBidTeam(bidId));
  dispatch(fetchBidMessages(bidId));
  dispatch(fetchBidAttachments(bidId));
  dispatch(fetchBidShipperHistory(bidId));
};

export const acceptPendingBid =
  (bidModeId, bidId, role, route) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(acceptActionRequest());
    try {
      await acceptBid(bidId, bidModeId, user.userRoleForManagementUser);
      dispatch(acceptActionSuccess());
      dispatch(fetchBidSummary({ bidId, role }));
      if (route === "summary") {
        dispatch(fetchBidTeam(bidId));
        dispatch(fetchBidMessages(bidId));
      }
      route === "details" && dispatch(fetchBidDetails(bidId));
    } catch {
      dispatch(acceptActionFailure());
    }
  };

export const archiveRejectedBid =
  (bidModeId, bidId, role, route) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(archiveActionRequest());
    try {
      await archiveBid(bidId, bidModeId, user.userRoleForManagementUser);
      dispatch(archiveActionSuccess());
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "details" && dispatch(fetchBidDetails(bidId));
    } catch {
      dispatch(archiveActionFailure());
    }
  };

export const fetchTLLaneCountByRegion = bidId => async dispatch => {
  dispatch(tlLaneCountByRegionRequest());

  try {
    const lanesByRegion = (await getTLLaneCountByRegion(bidId)).data;
    dispatch(tlLaneCountByRegionSuccess(lanesByRegion));
    dispatch(fetchRevenue(bidId));
  } catch {
    dispatch(tlLaneCountByRegionFailure());
  }
};

export const updateTeamMemberDetails =
  (
    teamContactUpdateRequest,
    removeBidSubscriberRequest,
    addBidSubscriberRequest,
    bidId,
    opportunityLeaderUserId = null,
    directorOfCustomerSuccessUserId = null
  ) =>
  async dispatch => {
    const putTeamContactRequest =
      teamContactUpdateRequest.length &&
      putTeamContact(teamContactUpdateRequest);
    const deleteBidSubscriberRequest =
      removeBidSubscriberRequest.length &&
      deleteBidSubscriber({
        bidId: parseInt(bidId),
        userIds: removeBidSubscriberRequest
      });
    const postBidSubscriberRequest =
      (addBidSubscriberRequest.length || opportunityLeaderUserId !== null || directorOfCustomerSuccessUserId !== null) &&
      postBidSubscriber({
        bidId: parseInt(bidId),
        userIds: addBidSubscriberRequest,
        opportunityLeaderUserId,
        directorOfCustomerSuccessUserId
      });

    let requestArr = [];
    putTeamContactRequest && requestArr.push(putTeamContactRequest);
    deleteBidSubscriberRequest && requestArr.push(deleteBidSubscriberRequest);
    postBidSubscriberRequest && requestArr.push(postBidSubscriberRequest);

    dispatch(requestTeamEdit());

    try {
      await axiosAll(requestArr);
      dispatch(resetTeamEditState());
      dispatch(fetchBidTeam(bidId));
    } catch (error) {
      // Check if the error is a 403 forbidden
      const isForbidden = error?.response?.status === httpResponseStatus.forbidden;
      dispatch(receiveTeamEditFailure({ isForbidden }));
    }
  };

export const closeBid =
  (bidId, role, route, closeLostWithCrmPayload) => async dispatch => {
    dispatch(closeBidActionRequest());
    try {
      if (closeLostWithCrmPayload) {
        await closeAllModesForBidLostWithCrm(bidId, closeLostWithCrmPayload);
      } else {
        await closeAllModesForBidLost(bidId);
      }
      dispatch(resetCloseBidActionState());
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "details" && dispatch(fetchBidDetails(bidId));
    } catch {
      dispatch(closeBidActionFailure());
    }
  };

export const closeBidWon = (bidId, role, route) => async (dispatch, getState) => {
  const { user } = getState().user;
  dispatch(closeBidWonActionRequest());
  try {
    await closeBidWonApi(bidId, user.userRoleForManagementUser);
    dispatch(resetCloseBidWonActionState());
    dispatch(fetchBidSummary({ bidId, role }));
    route === "summary" && dispatch(fetchBidMessages(bidId));
    route === "details" && dispatch(fetchBidDetails(bidId));
  } catch {
    dispatch(closeBidWonActionFailure());
  }
};

export const downloadAwardedLanes = (bidId, fileName) => dispatch => {
  dispatch(downloadAwardedLanesRequest());

  getAwardedLanes(bidId)
    .then(response => {
      saveFile(response.data, fileName);
      dispatch(downloadAwardedLanesSuccess());
    })
    .catch(() => dispatch(downloadAwardedLanesFailure()));
};

export const downloadShipperRatesAwardedLanes =
  (settings, fileName) => dispatch => {
    dispatch(downloadAwardedLanesRequest());

    getShipperRatesAwardedLanes(settings)
      .then(response => {
        saveFile(response.data, fileName);
        dispatch(downloadAwardedLanesSuccess());
      })
      .catch(() => dispatch(downloadAwardedLanesFailure()));
  };

export const closeModeSpecificBid =
  (bidId, role, route) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(closeBidModeActionRequest());
    try {
      await closeBidMode(bidId, user.userRoleForManagementUser);
      dispatch(resetCloseBidModeActionState());
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "details" && dispatch(fetchBidDetails(bidId));
    } catch {
      dispatch(closeBidModeActionFailure());
    }
  };

export const deleteDocument = (bidId, attachmentName) => async dispatch => {
  dispatch(requestDeleteItem());
  try {
    const request = { bidId: parseInt(bidId), attachmentName };
    await deleteAttachment(request);
    dispatch(resetDeleteItemState());
    dispatch(fetchBidAttachments(bidId));
  } catch {
    dispatch(receiveDeleteItemFailure());
  }
};

export const fetchRevenue = bidId => async dispatch => {
  dispatch(setRevenue(null));

  try {
    const revenue = (await getRevenue(bidId)).data;
    dispatch(setRevenue(revenue === "" ? null : revenue));
  } catch {
    dispatch(setRevenue(null));
  }
};

export const fetchShipperRatesStatus = shipperId => async dispatch => {
  try {
    const status = (await getShipperRatesStatus(shipperId)).data;
    dispatch(setShipperRatesEnabled(status));
  } catch {
    dispatch(setShipperRatesEnabled(false));
  }
};
